import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  computed,
  signal
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { Option } from "@interfaces/option";
import { Servico, ServicoSugestao } from "@interfaces/service";
import { NfseServiceService } from "@services/nfse-service.service";
import servicosLc116 from "@static/servicos.json";
import yesOrNot from "@static/yesOrNot.json";
import { Subscription } from "rxjs";
import { servicoExigeObra } from "src/app/utils/construcao-civil.util";
import { NFSE_DISCRIMINACAO_MAX } from "src/app/utils/nfse-discriminacao.util";

interface SubItemLc116 {
  item: string;
  descricao: string;
}

/** Como a lista vem no arquivo: ora "01.03" com subItems, ora "07.06.02" solto. */
interface EntradaLc116 {
  item: string;
  descricao: string;
  subItems?: SubItemLc116[];
}

interface ItemLc116 {
  item: string;
  descricao: string;
  subItens: SubItemLc116[];
}

const digitos = (value: unknown) => (value ?? "").toString().replace(/\D/g, "");

/**
 * Código do serviço válido: 6 dígitos (item + sub-item + desdobramento, ex.:
 * 070602, o código nacional) ou 4 dígitos (item + sub-item, ex.: 0706, o que
 * a prefeitura recebe no padrão municipal). O nf-server converte na emissão.
 */
const CODIGO_SERVICO = /^(\d{4}|\d{6})$/;

export { ITENS_CONSTRUCAO_CIVIL } from "src/app/utils/construcao-civil.util";

/**
 * Código de tributação do município coerente com o CNAE: quando o código tem
 * 9 dígitos (formato CNAE + desdobramento, usado por Fortaleza) os 7 primeiros
 * precisam ser o próprio CNAE — foi a causa das rejeições E550/E637. Outros
 * tamanhos são códigos próprios de outras prefeituras e passam sem checagem.
 */
export function codigoTributacaoCoerenteComCnae(
  control: AbstractControl
): ValidationErrors | null {
  const codigo = digitos(control.value);
  const cnae = digitos(control.parent?.get("cnae")?.value);

  if (codigo.length !== 9 || cnae.length !== 7) return null;

  return codigo.startsWith(cnae) ? null : { customInvalid: true };
}

/**
 * `servicos.json` mistura dois formatos: itens de dois níveis com `subItems` e
 * entradas já no formato item+sub-item ("07.06.02"). Aqui os dois viram a mesma
 * coisa — item ("07.06") com seus sub-itens ("02") —, que é o que o cadastro
 * precisa para montar o código de 6 dígitos.
 */
const LC116: ItemLc116[] = (() => {
  const itens = new Map<string, ItemLc116>();

  (servicosLc116 as unknown as EntradaLc116[]).forEach((entrada) => {
    const partes = (entrada.item ?? "").split(".");

    if (partes.length < 2) return;

    const item = `${partes[0]}.${partes[1]}`;
    const registro = itens.get(item) ?? {
      item,
      descricao: entrada.descricao,
      subItens: []
    };

    if (entrada.subItems?.length) {
      registro.descricao = entrada.descricao;
      entrada.subItems.forEach((sub) => registro.subItens.push(sub));
    } else {
      registro.subItens.push({
        item: partes[2] ?? "01",
        descricao: entrada.descricao
      });
    }

    itens.set(item, registro);
  });

  return [...itens.values()];
})();

/**
 * Cadastro de serviço da NFS-e na ordem em que um tributo depende do anterior
 * (a mesma do portal da NFS-e Nacional):
 *
 *   CNAE → item da LC 116 → sub-item → NBS → ISS → CST/classificação IBS/CBS
 *
 * Cada etapa só abre quando a anterior está preenchida, e o que o sistema
 * preenche sozinho é apenas o que vem de tabela oficial (lista de serviços da
 * LC 116 e a tabela CNAE → item do nf-server). Valores fiscais — NBS, alíquota
 * do ISS, CST e classificação tributária — continuam sendo informados por quem
 * cadastra: nada aqui é deduzido.
 *
 * Usado pela página "Novo serviço" e pela aba NFS-e do painel do contador, para
 * as duas telas terem o mesmo encadeamento.
 */
@Component({
  selector: "app-service-form",
  templateUrl: "./service-form.component.html",
  styleUrls: ["./service-form.component.scss"]
})
export class ServiceFormComponent {
  @Input() isLoading = false;
  @Input() submitText = "Salvar";
  @Input() submitIcon = "pi-save";

  /** Serviço em edição. Nulo/ausente = cadastro novo. */
  @Input() set servico(value: Servico | null | undefined) {
    this.hydrate(value ?? null);
  }

  @Output() save = new EventEmitter<Record<string, unknown>>();

  yon = yesOrNot;
  readonly discriminacaoMax = NFSE_DISCRIMINACAO_MAX;

  itensLc116: Option[] = LC116.map((item) => ({
    display: `${item.item} — ${item.descricao}`,
    value: item.item
  }));

  subItens = signal<Option[]>([]);
  sugestoes = signal<ServicoSugestao[]>([]);
  buscandoSugestoes = signal(false);

  sugestoesOptions = computed<Option[]>(() =>
    this.sugestoes().map((sugestao) => ({
      display: `${sugestao.codigoFormatado} — ${sugestao.descricao}`,
      value: sugestao.codigo
    }))
  );

  form = new FormGroup({
    cnae: new FormControl("", [
      Validators.required,
      Validators.pattern(/^\d{7}$/)
    ]),
    // Auxiliares de navegação da LC 116: não vão no payload (ver payload()).
    sugestao: new FormControl(""),
    itemLc116: new FormControl(""),
    subItem: new FormControl(""),
    codigo: new FormControl("", [
      Validators.required,
      Validators.pattern(CODIGO_SERVICO)
    ]),
    nome: new FormControl("", Validators.maxLength(120)),
    discriminacao: new FormControl("", [
      Validators.required,
      Validators.maxLength(NFSE_DISCRIMINACAO_MAX)
    ]),
    codigoTributacao: new FormControl("", codigoTributacaoCoerenteComCnae),
    codigoNbs: new FormControl("", [
      Validators.required,
      Validators.pattern(/^\d{9}$/)
    ]),
    quantidade: new FormControl(1),
    iss: new FormGroup({
      aliquota: new FormControl("", Validators.required),
      retido: new FormControl(false)
    }),
    ibscbs: new FormGroup({
      codigoOperacao: new FormControl("", [
        Validators.required,
        Validators.pattern(/^\d{6}$/)
      ]),
      valores: new FormGroup({
        tributacao: new FormGroup({
          cst: new FormControl("", [
            Validators.required,
            Validators.pattern(/^\d{3}$/)
          ]),
          cct: new FormControl("", [
            Validators.required,
            Validators.pattern(/^\d{6}$/)
          ])
        })
      }),
      operacaoPessoal: new FormControl<number | null>(null, [
        Validators.required,
        Validators.pattern(/^[01]$/)
      ])
    }),
    obra: new FormGroup({
      art: new FormControl(""),
      codigo: new FormControl(""),
      cei: new FormControl("")
    }),
    informacoesComplementares: new FormControl("")
  });

  /** Recalcula as travas a cada digitação. */
  private readonly tick = toSignal(this.form.valueChanges, {
    initialValue: null
  });

  cnaeOk = computed(() => {
    this.tick();
    return digitos(this.form.controls.cnae.value).length === 7;
  });

  servicoOk = computed(() => {
    this.tick();
    return (
      CODIGO_SERVICO.test(digitos(this.form.controls.codigo.value)) &&
      (this.form.controls.discriminacao.value ?? "").trim().length > 0
    );
  });

  nbsOk = computed(() => {
    this.tick();
    return /^\d{9}$/.test(digitos(this.form.controls.codigoNbs.value));
  });

  /** Item em que a prefeitura exige os dados da obra (ver ITENS_CONSTRUCAO_CIVIL). */
  obraExigida = computed(() => {
    this.tick();
    return servicoExigeObra(this.form.controls.codigo.value);
  });

  obraPreenchida = computed(() => {
    this.tick();
    const obra = this.form.controls.obra.getRawValue();
    return !!(obra.codigo ?? "").trim() || !!(obra.art ?? "").trim();
  });

  issOk = computed(() => {
    this.tick();
    return this.form.controls.iss.valid;
  });

  ibscbsOk = computed(() => {
    this.tick();
    return this.form.controls.ibscbs.valid;
  });

  // "Sem deduzir": o sistema nunca escreve por cima de uma discriminação
  // digitada — só preenche a que ele mesmo sugeriu.
  private discriminacaoAutomatica = "";
  private aplicando = false;
  private ultimoCnaeConsultado = "";
  private $: Subscription[] = [];

  constructor(
    private readonly nfseServiceService: NfseServiceService,
    destroyRef: DestroyRef
  ) {
    const controls = this.form.controls;

    this.$.push(
      controls.cnae.valueChanges.subscribe((value) => {
        // O validador do código de tributação depende do CNAE.
        controls.codigoTributacao.updateValueAndValidity({ emitEvent: false });
        this.onCnaeChange(digitos(value));
      }),
      controls.sugestao.valueChanges.subscribe((codigo) =>
        this.aplicarSugestao(digitos(codigo))
      ),
      controls.itemLc116.valueChanges.subscribe((item) =>
        this.onItemChange((item ?? "").toString())
      ),
      controls.subItem.valueChanges.subscribe((subItem) =>
        this.onSubItemChange((subItem ?? "").toString())
      ),
      controls.codigo.valueChanges.subscribe((codigo) =>
        this.sincronizarSelects(digitos(codigo))
      )
    );

    destroyRef.onDestroy(() => this.$.forEach((sub) => sub.unsubscribe()));
  }

  /**
   * CNAE completo: busca no nf-server os itens da LC 116 daquele CNAE. Com uma
   * única sugestão e o serviço ainda em branco, ela é aplicada; com várias, o
   * usuário escolhe. Nenhuma delas é obrigatória — dá para ignorar e escolher o
   * item na lista da LC 116.
   */
  private onCnaeChange(cnae: string) {
    if (cnae.length !== 7) {
      this.sugestoes.set([]);
      this.ultimoCnaeConsultado = "";
      return;
    }

    if (cnae === this.ultimoCnaeConsultado) return;

    this.ultimoCnaeConsultado = cnae;
    this.buscandoSugestoes.set(true);

    this.$.push(
      this.nfseServiceService.fetchSugestoesPorCnae(cnae).subscribe({
        next: (resposta) => {
          this.buscandoSugestoes.set(false);
          const sugestoes = resposta?.servicos ?? [];
          this.sugestoes.set(sugestoes);

          const semCodigo = !digitos(this.form.controls.codigo.value);

          if (sugestoes.length === 1 && semCodigo) {
            this.form.controls.sugestao.setValue(sugestoes[0].codigo);
          }
        },
        // Sugestão é conveniência: falhar aqui não pode travar o cadastro.
        error: () => {
          this.buscandoSugestoes.set(false);
          this.sugestoes.set([]);
        }
      })
    );
  }

  private aplicarSugestao(codigo: string) {
    const sugestao = this.sugestoes().find((item) => item.codigo === codigo);

    if (!sugestao) return;

    this.form.controls.codigo.setValue(sugestao.codigo);
    this.preencherDiscriminacao(sugestao.descricao);
  }

  private onItemChange(item: string) {
    const encontrado = LC116.find((lc) => lc.item === item);

    this.subItens.set(
      (encontrado?.subItens ?? []).map((sub) => ({
        display: `${sub.item} — ${sub.descricao}`,
        value: sub.item
      }))
    );

    if (this.aplicando || !encontrado) return;

    const subItens = encontrado.subItens ?? [];
    const atual = (this.form.controls.subItem.value ?? "").toString();
    const proximo =
      subItens.length === 1
        ? subItens[0].item
        : subItens.some((sub) => sub.item === atual)
          ? atual
          : "";

    this.form.controls.subItem.setValue(proximo);

    if (!proximo) {
      this.aplicando = true;
      this.form.controls.codigo.setValue("");
      this.aplicando = false;
    }
  }

  /** Item + sub-item formam o código de 6 dígitos que a emissão exige. */
  private onSubItemChange(subItem: string) {
    if (this.aplicando) return;

    const item = digitos(this.form.controls.itemLc116.value);

    if (item.length !== 4 || subItem.length !== 2) return;

    this.aplicando = true;
    this.form.controls.codigo.setValue(`${item}${subItem}`);
    this.aplicando = false;

    const descricao = LC116.find(
      (lc) => digitos(lc.item) === item
    )?.subItens.find((sub) => sub.item === subItem)?.descricao;

    if (descricao) this.preencherDiscriminacao(descricao);
  }

  /**
   * Código digitado à mão (ou vindo da sugestão) reposiciona os dois selects.
   * Com 4 dígitos (só item + sub-item da LC 116) posiciona o item e deixa o
   * desdobramento em branco, sem mexer no código digitado.
   */
  private sincronizarSelects(codigo: string) {
    if (this.aplicando || !CODIGO_SERVICO.test(codigo)) return;

    const item = `${codigo.slice(0, 2)}.${codigo.slice(2, 4)}`;
    const subItem = codigo.slice(4, 6);

    this.aplicando = true;
    this.carregarSubItens(item);
    this.form.controls.itemLc116.setValue(item);
    this.form.controls.subItem.setValue(subItem);
    this.aplicando = false;

    const descricao = LC116.find((lc) => lc.item === item)?.subItens.find(
      (sub) => sub.item === subItem
    )?.descricao;

    if (descricao) this.preencherDiscriminacao(descricao);
  }

  private carregarSubItens(item: string) {
    const encontrado = LC116.find((lc) => lc.item === item);

    this.subItens.set(
      (encontrado?.subItens ?? []).map((sub) => ({
        display: `${sub.item} — ${sub.descricao}`,
        value: sub.item
      }))
    );
  }

  private preencherDiscriminacao(descricao: string) {
    const atual = (this.form.controls.discriminacao.value ?? "").trim();

    if (atual && atual !== this.discriminacaoAutomatica) return;

    this.discriminacaoAutomatica = descricao;
    this.form.controls.discriminacao.setValue(descricao);
  }

  private hydrate(servico: Servico | null) {
    this.aplicando = true;
    this.sugestoes.set([]);
    this.ultimoCnaeConsultado = "";
    this.discriminacaoAutomatica = "";

    this.form.reset({
      quantidade: 1,
      iss: { retido: false }
    });

    if (!servico) {
      this.subItens.set([]);
      this.aplicando = false;
      return;
    }

    const codigo = digitos(servico.codigo);
    const codigoValido = CODIGO_SERVICO.test(codigo);
    const item = codigoValido
      ? `${codigo.slice(0, 2)}.${codigo.slice(2, 4)}`
      : "";

    this.carregarSubItens(item);

    this.form.patchValue({
      cnae: digitos(servico.cnae),
      itemLc116: item,
      subItem: codigoValido ? codigo.slice(4, 6) : "",
      codigo,
      nome: servico.nome ?? "",
      discriminacao: servico.discriminacao ?? "",
      codigoTributacao: servico.codigoTributacao ?? "",
      codigoNbs: digitos(servico.codigoNbs),
      quantidade: 1,
      iss: {
        aliquota:
          servico.iss?.aliquota !== undefined && servico.iss?.aliquota !== null
            ? servico.iss.aliquota.toString()
            : "",
        retido: !!servico.iss?.retido
      },
      ibscbs: {
        codigoOperacao: servico.ibscbs?.codigoOperacao ?? "",
        operacaoPessoal:
          servico.ibscbs?.operacaoPessoal !== undefined
            ? Number(servico.ibscbs?.operacaoPessoal)
            : null,
        valores: {
          tributacao: {
            cst: servico.ibscbs?.valores?.tributacao?.cst ?? "",
            cct: servico.ibscbs?.valores?.tributacao?.cct ?? ""
          }
        }
      },
      obra: {
        art: servico.obra?.art ?? "",
        codigo: servico.obra?.codigo ?? "",
        cei: servico.obra?.cei ?? ""
      },
      informacoesComplementares: servico.informacoesComplementares ?? ""
    });

    this.ultimoCnaeConsultado = digitos(servico.cnae);
    this.aplicando = false;
  }

  /** Payload do cadastro: os auxiliares da LC 116 ficam fora. */
  payload(): Record<string, unknown> {
    const { sugestao, itemLc116, subItem, ...dados } = this.form.getRawValue();

    return dados as Record<string, unknown>;
  }

  reset(servico: Servico | null = null) {
    this.hydrate(servico);
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.save.emit(this.payload());
  }
}
